
import { Treatment, PracticeLocation, SiteSettings } from '../types.ts';
import { storageService } from './storageService.ts';

const BLOB_TOKEN = "vercel_blob_rw_r47kiBCt0PiRWEEO_vuT6Bb92PjW8Dc64DFDm0pjthx1aWk";
const STORE_ID = "r47kiBCt0PiRWEEO";
const BASE_URL = `https://${STORE_ID.toLowerCase()}.public.blob.vercel-storage.com`;
// We gebruiken een kortere naam zonder versie-nummers om complicaties te voorkomen
const DATA_FILENAME = 'clinic_data.json'; 

export const apiService = {
  isCloudEnabled: false,
  connectionError: null as string | null,

  async getInitialData() {
    console.log("🔍 MD Cloud: Gegevens ophalen...");
    try {
      // Probeer eerst het hoofd-bestand
      const response = await fetch(`${BASE_URL}/${DATA_FILENAME}?t=${Date.now()}`, {
        cache: 'no-store',
        mode: 'cors'
      });

      if (response.ok) {
        const cloudData = await response.json();
        if (cloudData && cloudData.treatments) {
          storageService.saveAll(cloudData);
          this.isCloudEnabled = true;
          return cloudData;
        }
      }
    } catch (e) {
      console.log("ℹ️ Cloud: Nog geen gegevens gevonden, lokale defaults worden gebruikt.");
    }
    
    return storageService.getAllData();
  },

  async syncToCloud(data: any): Promise<void> {
    try {
      console.log("☁️ MD Cloud: Opslaan...");
      storageService.saveAll(data);

      // CRUCIAAL: We verwijderen 'x-add-random-suffix' en 'x-api-version' 
      // omdat deze de CORS-preflight blokkeren in de browser.
      const response = await fetch(`https://blob.vercel-storage.com/${DATA_FILENAME}`, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          'Authorization': `Bearer ${BLOB_TOKEN}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Server reageerde niet correct");
      }
      
      this.isCloudEnabled = true;
      console.log("✅ MD Cloud: Succesvol opgeslagen.");
    } catch (e: any) {
      console.error("❌ MD Cloud Sync Fout:", e);
      throw new Error(e.message || "Verbindingsfout met de cloud");
    }
  },

  async login(username: string, password: string): Promise<boolean> {
    const data = storageService.getAllData();
    const { adminUsername, adminPassword } = data.settings;
    if (username === adminUsername && password === adminPassword) {
      localStorage.setItem('md_admin_token', 'true');
      return true;
    }
    return false;
  },

  async checkAuth(): Promise<boolean> {
    return !!localStorage.getItem('md_admin_token');
  },

  async logout(): Promise<void> {
    localStorage.removeItem('md_admin_token');
  },

  async saveTreatments(treatments: Treatment[]): Promise<void> {
    const data = storageService.getAllData();
    data.treatments = treatments;
    await this.syncToCloud(data);
  },

  async saveLocations(locations: PracticeLocation[]): Promise<void> {
    const data = storageService.getAllData();
    data.locations = locations;
    await this.syncToCloud(data);
  },

  async updateSettings(settings: SiteSettings): Promise<void> {
    const data = storageService.getAllData();
    data.settings = settings;
    await this.syncToCloud(data);
  },

  async uploadImage(file: File): Promise<string> {
    // We behouden spaties niet in de bestandsnaam om URL problemen te voorkomen
    const cleanName = file.name.replace(/\s+/g, '_');
    const filename = `uploads/${Date.now()}-${cleanName}`;
    
    const response = await fetch(`https://blob.vercel-storage.com/${filename}`, {
      method: 'PUT',
      body: file,
      headers: {
        'Authorization': `Bearer ${BLOB_TOKEN}`
      }
    });

    if (!response.ok) throw new Error("Upload mislukt.");
    const result = await response.json();
    return result.url;
  }
};
